/**
 * ProductOptions — renders variant/option selectors (size, color, etc.)
 */
import { isDefaultSingleVariantOption } from "@modules/shopify/product-utils";
import { useTranslation } from "react-i18next";

interface SelectedOption {
  name: string;
  value: string;
}

interface BaseVariant {
  id: string;
  availableForSale: boolean;
  price?: {
    amount: string;
    currencyCode: string;
  };
  selectedOptions?: Array<SelectedOption>;
}

interface ProductOption {
  name: string;
  values: Array<string>;
}

interface ProductOptionsProps<TVariant extends BaseVariant> {
  options: Array<ProductOption>;
  variants: Array<TVariant>;
  selectedVariant: TVariant | undefined;
  onSelectVariant: (variant: TVariant) => void;
}

export function ProductOptions<TVariant extends BaseVariant>({
  options,
  variants,
  selectedVariant,
  onSelectVariant,
}: Readonly<ProductOptionsProps<TVariant>>) {
  return (
    <>
      {options
        .filter(
          (option) =>
            !isDefaultSingleVariantOption({
              name: option.name,
              value: option.values[0] ?? "",
            }),
        )
        .map((option) => (
          <OptionGroup
            key={option.name}
            option={option}
            variants={variants}
            selectedVariant={selectedVariant}
            onSelectVariant={onSelectVariant}
          />
        ))}
    </>
  );
}

function OptionGroup<TVariant extends BaseVariant>({
  option,
  variants,
  selectedVariant,
  onSelectVariant,
}: Readonly<{
  option: ProductOption;
  variants: Array<TVariant>;
  selectedVariant: TVariant | undefined;
  onSelectVariant: (variant: TVariant) => void;
}>) {
  const { i18n, t } = useTranslation();

  // Translate option name using the optionName mapping, fallback to original name
  const translatedOptionName = t(
    `SingleProductPage.optionName.${option.name}`,
    {
      defaultValue: option.name,
    },
  );

  const getVariantForValue = (value: string) => {
    const nextSelectedOptions = [
      ...(selectedVariant?.selectedOptions ?? []).filter(
        (selectedOption) => selectedOption.name !== option.name,
      ),
      { name: option.name, value },
    ];

    return variants.find((variant) =>
      nextSelectedOptions.every((selectedOption) =>
        variant.selectedOptions?.some(
          (variantOption) =>
            variantOption.name === selectedOption.name &&
            variantOption.value === selectedOption.value,
        ),
      ),
    );
  };

  const getPriceDelta = (variant: TVariant | undefined) => {
    const variantPrice = Number(variant?.price?.amount);
    const comparableVariants = variants.filter((candidate) =>
      (selectedVariant?.selectedOptions ?? [])
        .filter((selectedOption) => selectedOption.name !== option.name)
        .every((selectedOption) =>
          candidate.selectedOptions?.some(
            (candidateOption) =>
              candidateOption.name === selectedOption.name &&
              candidateOption.value === selectedOption.value,
          ),
        ),
    );
    const basePrice = Math.min(
      ...comparableVariants
        .map((candidate) => Number(candidate.price?.amount))
        .filter(Number.isFinite),
    );

    if (!Number.isFinite(variantPrice) || !Number.isFinite(basePrice)) {
      return null;
    }

    const delta = variantPrice - basePrice;

    if (delta <= 0) {
      return null;
    }

    return {
      amount: delta,
      currencyCode: variant?.price?.currencyCode ?? "EUR",
    };
  };

  return (
    <div className="mb-6">
      <label className="mb-2.5 block text-small font-semibold text-text-1">
        {translatedOptionName}
      </label>
      <div className="flex flex-wrap gap-2">
        {option.values.map((value) => {
          const matchingVariant = getVariantForValue(value);
          const isSelected = selectedVariant?.selectedOptions?.some(
            (opt) => opt.name === option.name && opt.value === value,
          );
          const isAvailable = matchingVariant?.availableForSale;
          const priceDelta = getPriceDelta(matchingVariant);

          return (
            <OptionButton
              key={value}
              value={value}
              locale={i18n.language}
              priceDelta={priceDelta}
              isSelected={!!isSelected}
              isAvailable={!!isAvailable}
              onClick={() =>
                matchingVariant && onSelectVariant(matchingVariant)
              }
            />
          );
        })}
      </div>
    </div>
  );
}

function getOptionClassName(isSelected: boolean, isAvailable: boolean) {
  const base =
    "min-w-12 rounded-full px-4 py-2.5 text-small font-medium tabular-nums transition-[background-color,color,box-shadow,scale] duration-150 ease-out active:scale-[0.96]";
  if (isSelected) return `${base} bg-text-1 text-white`;
  if (isAvailable)
    return `${base} bg-white text-text-1 ring-1 ring-black/[0.08] hover:ring-text-1`;
  return `${base} cursor-not-allowed bg-white/60 text-text-4 line-through`;
}

function OptionButton({
  value,
  locale,
  priceDelta,
  isSelected,
  isAvailable,
  onClick,
}: Readonly<{
  value: string;
  locale: string;
  priceDelta: { amount: number; currencyCode: string } | null;
  isSelected: boolean;
  isAvailable: boolean;
  onClick: () => void;
}>) {
  const formattedDelta = priceDelta
    ? new Intl.NumberFormat(locale, {
        style: "currency",
        currency: priceDelta.currencyCode,
      }).format(priceDelta.amount)
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isAvailable}
      className={getOptionClassName(isSelected, isAvailable)}
    >
      <span>{value}</span>
      {formattedDelta && <span className="ml-1">+{formattedDelta}</span>}
    </button>
  );
}
